/**
 * RosterView — per-person allocation heatmap inside Workspace Overview.
 *
 * Consumes /api/named-resources/allocation and renders each named
 * resource as a color-coded "load bar" row. Click to expand and see
 * the underlying estimate share. Leadership-facing: the goal is a
 * one-glance answer to "who is over-booked right now?"
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import Sparkline from '../../components/Sparkline';
import {
  Loader2, Users, AlertTriangle, ChevronRight, ChevronDown,
  Mail, ExternalLink, ArrowRight,
} from 'lucide-react';
import { HighlightMatch } from './shared';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Load thresholds (weeks of allocated work against weekly capacity).
// Tuned so the natural read is "idle / healthy / loaded / overbooked".
const LOAD_BUCKETS = [
  { id: 'idle',       max: 0.25, label: 'Idle',       bar: 'bg-slate-200',   text: 'text-slate-500',   chip: 'bg-slate-100 text-slate-600' },
  { id: 'healthy',    max: 2,    label: 'Healthy',    bar: 'bg-emerald-400', text: 'text-emerald-700', chip: 'bg-emerald-100 text-emerald-700' },
  { id: 'loaded',     max: 4,    label: 'Loaded',     bar: 'bg-amber-400',   text: 'text-amber-700',   chip: 'bg-amber-100 text-amber-700' },
  { id: 'overbooked', max: 9999, label: 'Overbooked', bar: 'bg-rose-500',    text: 'text-rose-700',    chip: 'bg-rose-100 text-rose-700' },
];

const bucketFor = (weeks) => {
  const w = weeks == null ? 0 : weeks;
  return LOAD_BUCKETS.find(b => w <= b.max) || LOAD_BUCKETS[LOAD_BUCKETS.length - 1];
};

// Cap the visual bar at 6 weeks so an "Overbooked × 100" record doesn't
// blow out the column width. The numeric value still renders verbatim.
const BAR_CAP_WEEKS = 6;

// ─── Sparkline helpers ────────────────────────────────────────────────
// We compute per-resource weekly hours from the workspace_overview
// `portfolioData` (passed in as a prop) — every project there already
// carries `role_allocation[*].weekly_hours[]`. Divide each project's
// per-role hours by `peers_in_role` to get the slice that lands on this
// particular named person.

function computeWeeklyLoadForRole(portfolioData, role, peers) {
  if (!portfolioData || !portfolioData.projects || !peers || peers < 1) return null;
  const weeks = portfolioData.date_range?.weeks || 0;
  if (!weeks) return null;
  const out = new Array(weeks).fill(0);
  const contributing = [];
  for (const p of portfolioData.projects) {
    const ra = (p.role_allocation || []).find(r => r.role === role);
    if (!ra || !Array.isArray(ra.weekly_hours)) continue;
    let projTotal = 0;
    for (let i = 0; i < weeks; i++) {
      const share = (ra.weekly_hours[i] || 0) / peers;
      out[i] += share;
      projTotal += share;
    }
    if (projTotal > 0.5) {
      contributing.push({ id: p.id, name: p.name, hours: projTotal });
    }
  }
  return { weekly_hours: out, contributing };
}

function Sparkline_LEGACY_REMOVED() { return null; }

export function RosterView({ onOpenEstimate, portfolioData, weekLabels, todayIdx, sortField = 'name', sortDir = 'asc', searchQuery = '' }) {
  const [loading, setLoading] = useState(true);
  const [allocation, setAllocation] = useState(null);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [bucketFilter, setBucketFilter] = useState(null); // 'idle' | 'healthy' | 'loaded' | 'overbooked' | null

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API_URL}/api/named-resources/allocation`, { withCredentials: true })
      .then(r => { if (!cancelled) setAllocation(r.data); })
      .catch(e => { if (!cancelled) setError(e?.response?.data?.detail || e.message || 'Failed to load roster'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Per-bucket counts for the filter chip bar
  const bucketCounts = useMemo(() => {
    const out = { all: 0, idle: 0, healthy: 0, loaded: 0, overbooked: 0 };
    if (!allocation?.resources) return out;
    for (const r of allocation.resources) {
      out.all += 1;
      out[bucketFor(r.weeks_of_work).id] += 1;
    }
    return out;
  }, [allocation]);

  const filteredResources = useMemo(() => {
    if (!allocation?.resources) return [];
    let base = bucketFilter
      ? allocation.resources.filter(r => bucketFor(r.weeks_of_work).id === bucketFilter)
      : allocation.resources;
    // v261 — Portfolio quick search: keep only people contributing to a
    // matching project so the Roster tracks the same filter as other tabs.
    const q = (searchQuery || '').trim().toLowerCase();
    if (q) base = base.filter(r => (r.estimates || []).some(e => (e.name || '').toLowerCase().includes(q)));
    // v261 — Portfolio "Order by" applies to people rows by name; the
    // start-date field has no person equivalent so it keeps API order.
    if (sortField !== 'name') return base;
    const dir = sortDir === 'desc' ? -1 : 1;
    return [...base].sort((a, b) => dir * (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
  }, [allocation, bucketFilter, sortField, sortDir, searchQuery]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="roster-view-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#0F2040]" />
      </div>
    );
  }

  if (error) {
    return (
      <Card data-testid="roster-view-error">
        <CardContent className="py-8 text-center text-sm text-rose-600">{error}</CardContent>
      </Card>
    );
  }

  const resources = allocation?.resources || [];
  const unfilled = allocation?.unfilled_roles || [];
  const summary = allocation?.summary || {};

  if (resources.length === 0) {
    return (
      <Card data-testid="roster-view-empty">
        <CardContent className="py-12 text-center">
          <Users className="w-8 h-8 mx-auto text-slate-300 mb-2" />
          <p className="text-sm text-slate-600 font-medium">No named resources yet.</p>
          <p className="text-xs text-slate-400 mt-1">
            Add people in <strong>Settings → Named Resources</strong> to surface per-person allocation here.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="roster-view">
      {/* Summary strip */}
      <Card>
        <CardContent className="py-3">
          <div className="flex items-center gap-4 flex-wrap text-xs text-slate-600">
            <span data-testid="roster-summary-total">
              <strong className="text-[#0F2040] text-base">{resources.length}</strong> people · {summary.total_estimates || 0} active estimates
            </span>
            <span className="text-slate-300">|</span>
            <span className="flex items-center gap-2 flex-wrap" data-testid="roster-bucket-chips">
              {LOAD_BUCKETS.map(b => {
                const active = bucketFilter === b.id;
                const n = bucketCounts[b.id];
                return (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => setBucketFilter(active ? null : b.id)}
                    data-testid={`roster-bucket-${b.id}`}
                    className={`px-2 py-0.5 rounded-full border text-[11px] transition ${
                      active
                        ? `${b.chip} border-current`
                        : 'border-slate-200 text-slate-500 hover:border-slate-300 bg-white'
                    }`}
                  >
                    <span className={`inline-block w-2 h-2 rounded-full ${b.bar} mr-1 align-middle`} />
                    {b.label} · {n}
                  </button>
                );
              })}
              {bucketFilter && (
                <button
                  type="button"
                  onClick={() => setBucketFilter(null)}
                  className="text-[11px] text-slate-400 hover:text-slate-600 underline"
                  data-testid="roster-bucket-clear"
                >
                  Clear
                </button>
              )}
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Heatmap rows */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Users className="w-4 h-4 text-[#4ABFCF]" />
            Per-person load
          </CardTitle>
          <p className="text-[11px] text-slate-400">
            Per-week projected load vs each person's weekly capacity. Bars colour-code load level; the dashed line marks capacity, the navy line is today. Computed from the current portfolio's role hours divided equally among peers in each role.
          </p>
        </CardHeader>
        <CardContent>
          {filteredResources.length === 0 ? (
            <p className="text-xs text-slate-400 italic py-6 text-center" data-testid="roster-no-match">
              No resources match this filter.
            </p>
          ) : (
            <div className="space-y-2">
              {filteredResources.map(r => {
                const bucket = bucketFor(r.weeks_of_work);
                const isOpen = !!expanded[r.id];
                const widthPct = Math.min(100, ((r.weeks_of_work || 0) / BAR_CAP_WEEKS) * 100);
                const sparkline = computeWeeklyLoadForRole(portfolioData, r.role, Math.max(1, r.peers_in_role || 1));
                const peakHours = sparkline ? Math.max(0, ...sparkline.weekly_hours) : 0;
                const peakIdx = sparkline ? sparkline.weekly_hours.indexOf(peakHours) : -1;
                const peakLabel = peakIdx >= 0 ? (weekLabels?.[peakIdx] || null) : null;
                return (
                  <div
                    key={r.id}
                    className="border border-slate-100 rounded-lg p-3 bg-white hover:border-slate-200 transition"
                    data-testid={`roster-row-${r.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => setExpanded(prev => ({ ...prev, [r.id]: !prev[r.id] }))}
                      className="w-full flex items-center gap-3 text-left"
                      data-testid={`roster-row-toggle-${r.id}`}
                    >
                      {isOpen
                        ? <ChevronDown className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                        : <ChevronRight className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />}
                      <div className="flex-shrink-0 min-w-[180px]">
                        <div className="font-medium text-sm text-[#0F2040]">{r.name}</div>
                        <div className="flex items-center gap-1 mt-0.5">
                          <Badge variant="secondary" className="text-[10px] py-0">
                            {r.role}
                          </Badge>
                          {r.peers_in_role > 1 && (
                            <span className="text-[10px] text-slate-400">+{r.peers_in_role - 1} peer{r.peers_in_role > 2 ? 's' : ''}</span>
                          )}
                        </div>
                      </div>

                      {/* Sparkline or fallback aggregate bar */}
                      <div className="flex-1 min-w-0">
                        {sparkline ? (
                          <Sparkline
                            weeklyHours={sparkline.weekly_hours}
                            capacityPerWeek={r.weekly_capacity_hours || 40}
                            weekLabels={weekLabels}
                            todayIdx={todayIdx}
                            testid="roster-sparkline"
                          />
                        ) : (
                          <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden relative">
                            <div
                              className={`h-full ${bucket.bar} transition-all`}
                              style={{ width: `${widthPct}%` }}
                              data-testid={`roster-bar-${r.id}`}
                            />
                            <div
                              className="absolute top-0 bottom-0 border-l border-slate-300"
                              style={{ left: `${(1 / BAR_CAP_WEEKS) * 100}%` }}
                              aria-hidden="true"
                            />
                          </div>
                        )}
                      </div>

                      {/* Numeric readout */}
                      <div className="flex-shrink-0 text-right" style={{ minWidth: 160 }}>
                        <div className={`text-sm font-semibold tabular-nums ${bucket.text}`}>
                          {(r.allocated_hours || 0).toLocaleString()}h
                        </div>
                        <div className="text-[10px] text-slate-400 tabular-nums">
                          {sparkline && peakHours > 0 ? (
                            <span data-testid={`roster-peak-${r.id}`}>
                              Peak {peakHours.toFixed(1)}h{peakLabel ? ` · wk of ${peakLabel.slice(5)}` : ''} · {r.weekly_capacity_hours}h cap
                              {peakHours > (r.weekly_capacity_hours || 40) * 3 && (
                                <span
                                  className="ml-1 px-1 py-px rounded bg-rose-100 text-rose-700 text-[9px] font-semibold"
                                  data-testid={`roster-overflow-${r.id}`}
                                  title="Peak load exceeds 3× weekly capacity — bars saturate the sparkline"
                                >
                                  {Math.round(peakHours / (r.weekly_capacity_hours || 40))}× cap
                                </span>
                              )}
                            </span>
                          ) : (
                            <span>{(r.weeks_of_work ?? 0).toFixed(1)}w · {r.weekly_capacity_hours}h/wk</span>
                          )}
                        </div>
                      </div>

                      {/* Bucket chip */}
                      <Badge className={`text-[10px] py-0 ${bucket.chip} flex-shrink-0`}>
                        {bucket.id === 'overbooked' && <AlertTriangle className="w-2.5 h-2.5 mr-0.5 inline" />}
                        {bucket.label}
                      </Badge>
                    </button>

                    {/* Expanded: estimates rollup */}
                    {isOpen && (
                      <div className="mt-3 ml-7 pt-3 border-t border-slate-100" data-testid={`roster-detail-${r.id}`}>
                        {r.email && (
                          <a
                            href={`mailto:${r.email}`}
                            className="text-[11px] text-[#4ABFCF] hover:underline inline-flex items-center gap-1 mb-2"
                          >
                            <Mail className="w-3 h-3" />
                            {r.email}
                          </a>
                        )}
                        {r.estimates?.length === 0 ? (
                          <p className="text-[11px] text-slate-400 italic">No active estimates carry hours for this role.</p>
                        ) : (
                          <div className="space-y-1">
                            <div className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">
                              Contributing estimates ({r.estimates.length})
                            </div>
                            <ul className="space-y-0.5">
                              {r.estimates.slice(0, 12).map((e, idx) => (
                                <li
                                  key={`${e.id || 'noid'}-${idx}`}
                                  className="flex items-center justify-between text-[11px] py-0.5"
                                  data-testid={`roster-estimate-${r.id}-${e.id || idx}`}
                                >
                                  <button
                                    type="button"
                                    onClick={() => e.id && onOpenEstimate && onOpenEstimate(e.id)}
                                    disabled={!e.id}
                                    className="text-[#0F2040] hover:text-[#4ABFCF] hover:underline inline-flex items-center gap-1 truncate max-w-md disabled:opacity-60 disabled:no-underline disabled:cursor-default"
                                  >
                                    <ExternalLink className="w-2.5 h-2.5 flex-shrink-0" />
                                    <span className="truncate"><HighlightMatch text={e.name} query={searchQuery} /></span>
                                  </button>
                                  <span className="text-slate-500 tabular-nums flex-shrink-0 ml-2">
                                    {e.hours_share.toLocaleString()}h
                                    {e.assignment_pct != null && (
                                      <span
                                        className="ml-1 px-1 py-px rounded bg-indigo-100 text-indigo-700 text-[9px] font-semibold"
                                        title={`Explicit assignment: ${e.assignment_pct}% of this estimate's ${r.role} hours`}
                                      >
                                        {e.assignment_pct}%
                                      </span>
                                    )}
                                    {e.assignment_pct == null && r.peers_in_role > 1 && <span className="text-slate-300"> · 1/{r.peers_in_role}</span>}
                                  </span>
                                </li>
                              ))}
                              {r.estimates.length > 12 && (
                                <li className="text-[10px] text-slate-400 italic pt-1">
                                  + {r.estimates.length - 12} more estimate{r.estimates.length - 12 === 1 ? '' : 's'}
                                </li>
                              )}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Unfilled roles */}
      {unfilled.length > 0 && (
        <Card className="border-amber-200 bg-amber-50/40" data-testid="roster-unfilled-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-amber-800">
              <AlertTriangle className="w-4 h-4" />
              Unfilled roles ({unfilled.length})
            </CardTitle>
            <p className="text-[11px] text-amber-700">
              Roles with allocated hours on active estimates but no named owners. Hours land nowhere on this heatmap.
            </p>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {unfilled.map(u => (
                <Badge
                  key={u.role}
                  className="bg-amber-100 text-amber-800 border-amber-300 text-[11px]"
                  data-testid={`roster-unfilled-${u.role}`}
                >
                  {u.role} — {u.label} · {u.total_hours.toLocaleString()}h
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default RosterView;
